import { v4 as uuidv4 } from 'uuid';
import { getAvailableCommands } from '../actions/commandRegistry.js';

const NATIVE_COMMAND_IDS = new Set([
    'backlight.off', 'backlight.on', 'reboot', 'screenshot',
    'idle.off', 'idle.short', 'idle.long',
    'page.next', 'page.prev', 'page.back', 'page.goto',
    'clearpage.all', 'page.clear',
]);

const schemaCommandNodes = getAvailableCommands()
    .filter(command => command.id !== 'audio.play' && !NATIVE_COMMAND_IDS.has(command.id))
    .map(command => ({
        type: 'command',
        paletteId: `command:${command.id}`,
        commandId: command.id,
        label: command.label,
        icon: command.category === 'Audio' ? '🔊' : '⌘',
        defaultData: {
            commandId: command.id,
            parameters: Object.fromEntries((command.parameters ?? [])
                .map(parameter => [parameter.id, parameter.scriptDefault ?? parameter.default ?? ''])),
        },
    }));

const schemaCommandGroups = schemaCommandNodes.reduce((groups, command) => {
    const category = getAvailableCommands().find(definition => definition.id === command.commandId)?.category ?? 'Other';
    (groups[category] ??= []).push(command);
    return groups;
}, {});

const schemaCommandCategories = Object.entries(schemaCommandGroups).map(([category, nodes]) => ({
    label: `${category} commands`,
    color: category === 'Audio' ? '#0e7490' : '#0891b2',
    nodes,
}));

export const NODE_CATEGORIES = [
    {
        label: 'Page',
        color: '#2563eb',
        nodes: [
            { type: 'gotoPage', label: 'Go to Page', icon: '▶', defaultData: { pageTarget: 'next' } },
            { type: 'clearPage', label: 'Clear Page', icon: '🗑', defaultData: { page: 'all' } },
        ],
    },
    {
        label: 'Object',
        color: '#7c3aed',
        nodes: [
            { type: 'templateRef', label: 'Template Page', icon: '⬡', defaultData: { templatePageId: '' } },
            { type: 'setProperty', label: 'Set Property', icon: '✏', defaultData: { pageId: '', objectId: '', property: '', value: '' } },
            { type: 'jsonl', label: 'JSONL (raw)', icon: '{ }', defaultData: { payload: '' } },
        ],
    },
    {
        label: 'Display',
        color: '#d97706',
        nodes: [
            { type: 'backlight', label: 'Set Backlight', icon: '☀', defaultData: { state: true, brightness: 128 } },
            { type: 'moodlight', label: 'Set Moodlight', icon: '🌈', defaultData: { state: true, r: 255, g: 0, b: 0 } },
        ],
    },
    {
        label: 'Control',
        color: '#16a34a',
        nodes: [
            { type: 'delay', label: 'Delay', icon: '⏱', defaultData: { duration: 500 } },
            { type: 'runScript', label: 'Run Script', icon: '▶', defaultData: { filename: '' } },
            { type: 'idle', label: 'Set Idle', icon: '💤', defaultData: { idleState: 'off' } },
        ],
    },
    {
        label: 'GPIO',
        color: '#ca8a04',
        nodes: [
            { type: 'output', label: 'Set Output', icon: '⚙', defaultData: { outputPin: 0, state: true } },
        ],
    },
    {
        label: 'System',
        color: '#dc2626',
        nodes: [
            { type: 'reboot', label: 'Reboot', icon: '↺', defaultData: {} },
            { type: 'screenshot', label: 'Screenshot', icon: '📷', defaultData: {} },
        ],
    },
    ...schemaCommandCategories,
];

export function createNode(type, defaultData, position = { x: 200, y: 200 }) {
    return {
        id: `${type}-${uuidv4().slice(0, 8)}`,
        type,
        position,
        data: { ...defaultData },
    };
}
